/**
 * 「员工用不了某个模型」逐层诊断的结果契约（N-156）。
 *
 * 生产者：`@dosia/core` `diagnoseModelAvailability`（判据全部调用下发链上的同一批函数），经超管后台
 * `GET /api/platform/model-diagnosis` 返回；消费者：超管后台「平台模型治理 → 诊断」页签。本文件只放
 * 形状、原因码表与运行时 guard（admin-portal-web 只依赖 shared + ui，拿不到 core）。
 *
 * 五层按排查顺序：平台策展 → 组织 / 角色圈定 → 企业下发（员工 bundle 或网关公开清单）→ 桌面本机（按下发
 * 推算）→ 网关。三种用法：Claude 引擎（原生 Anthropic 腿）、Pi 引擎（`[pi]` 变体）、干活模型（网关翻译的
 * Anthropic 腿，归干活模型池）。每条结论是 (层, 用法, 结论, 原因码, 参数)；文案由消费端按原因码本地化。
 */
export const MODEL_DIAGNOSIS_LAYERS = ["platform", "selection", "bundle", "desktop", "gateway"];
export const MODEL_DIAGNOSIS_USES = ["claude", "pi", "work"];
/**
 * - `pass` 这一层放行；`warn` 放行但要人看一眼（例：未审阅、由下发前对齐自动并入）；
 * - `blocked` 这一层的配置把它拦下；`unavailable` 上游此刻暂不可用（员工端会藏起来）；
 * - `unknown` 判不了（数据取不到）；`not_applicable` 这种用法不适用 / 这一层没被要求或没走到。
 */
export const MODEL_DIAGNOSIS_VERDICTS = ["pass", "warn", "blocked", "unavailable", "unknown", "not_applicable"];
/** 原因码（消费端 i18n 键的后缀；新增一个码 = 7 份 locale 各加一句）。 */
export const MODEL_DIAGNOSIS_CODES = [
    // 通用
    "no_leg",
    "not_requested",
    "not_reached",
    "catalog_not_live",
    // 平台策展
    "pool_open",
    "pool_selected",
    "pool_auto_merged",
    "pool_declined",
    "pool_not_candidate",
    "pool_not_listed",
    "pool_closed",
    "platform_filtered",
    // 组织 / 角色圈定
    "selection_open",
    "selection_listed",
    "selection_auto_merged",
    "selection_declined",
    "selection_not_listed",
    // 企业下发
    "supply_rows",
    "supply_unavailable",
    "supply_no_credential",
    "supply_missing",
    "supply_not_in_catalog",
    "member_key_missing",
    "member_not_registered",
    "bundle_fetch_failed",
    // 桌面本机（推算）
    "desktop_ok",
    "desktop_router_missing",
    "desktop_group_hidden",
    "desktop_hidden_unavailable",
    "desktop_pi_menu_missing",
    "desktop_work_missing",
    // 网关
    "gateway_reports_available",
    "gateway_handoff",
];
/**
 * 该谁去处理：超管（平台三个池）/ 组织管理员（组织与角色圈定）/ 网关（上游账号、中转渠道、分组 key、零账号行，
 * 以及一切「上游此刻暂不可用」——平台池此刻不把它当候选也是这个原因的连带结果）/ 桌面本机 / 判不了。
 */
export const MODEL_DIAGNOSIS_OWNERS = ["platform_admin", "org_admin", "gateway", "desktop", "unknown"];
const isRecord = (v) => !!v && typeof v === "object" && !Array.isArray(v);
const isStr = (v) => typeof v === "string";
const oneOf = (list, v) => isStr(v) && list.includes(v);
const isStrList = (v) => Array.isArray(v) && v.every(isStr);
function parseParams(raw) {
    if (raw === undefined)
        return undefined;
    if (!isRecord(raw))
        return null;
    const out = {};
    for (const [k, v] of Object.entries(raw)) {
        if (isStr(v) || typeof v === "number" || typeof v === "boolean" || isStrList(v))
            out[k] = v;
        else
            return null;
    }
    return out;
}
function parseFinding(raw) {
    if (!isRecord(raw))
        return null;
    const { layer, use, verdict, code, scope } = raw;
    if (!oneOf(MODEL_DIAGNOSIS_LAYERS, layer) || !oneOf(MODEL_DIAGNOSIS_USES, use))
        return null;
    if (!oneOf(MODEL_DIAGNOSIS_VERDICTS, verdict) || !oneOf(MODEL_DIAGNOSIS_CODES, code))
        return null;
    if (scope !== undefined && scope !== "org" && scope !== "role")
        return null;
    const params = parseParams(raw.params);
    if (params === null)
        return null;
    return { layer, use, verdict, code, ...(scope ? { scope } : {}), ...(params ? { params } : {}) };
}
function parseConclusion(raw) {
    if (!isRecord(raw))
        return null;
    const { use, layer, verdict, code, owner } = raw;
    if (!oneOf(MODEL_DIAGNOSIS_USES, use) || !oneOf(MODEL_DIAGNOSIS_LAYERS, layer))
        return null;
    if (!oneOf(MODEL_DIAGNOSIS_VERDICTS, verdict) || !oneOf(MODEL_DIAGNOSIS_CODES, code))
        return null;
    if (!oneOf(MODEL_DIAGNOSIS_OWNERS, owner))
        return null;
    return { use, layer, verdict, code, owner };
}
function parseMember(raw) {
    if (!isRecord(raw) || typeof raw.registered !== "boolean")
        return null;
    if (raw.lastFetchedAt !== null && !isStr(raw.lastFetchedAt))
        return null;
    let lastError = null;
    if (raw.lastError !== null && raw.lastError !== undefined) {
        if (!isRecord(raw.lastError) || !isStr(raw.lastError.message))
            return null;
        lastError = {
            message: raw.lastError.message,
            ...(typeof raw.lastError.httpStatus === "number" ? { httpStatus: raw.lastError.httpStatus } : {}),
            ...(isStr(raw.lastError.at) ? { at: raw.lastError.at } : {}),
        };
    }
    return {
        registered: raw.registered,
        lastFetchedAt: raw.lastFetchedAt,
        lastError,
        ...(isStr(raw.memberName) ? { memberName: raw.memberName } : {}),
        ...(typeof raw.online === "boolean" ? { online: raw.online } : {}),
    };
}
/** 反序列化边界 guard：形状不对一律 undefined（调用方显「诊断结果无法解析」，不猜）。 */
export function parseModelAvailabilityDiagnosis(raw) {
    if (!isRecord(raw) || raw.version !== 1)
        return undefined;
    if (!isStr(raw.modelId) || !raw.modelId || !isStr(raw.generatedAt) || typeof raw.catalogLive !== "boolean")
        return undefined;
    if (raw.supplySource !== "member_bundle" && raw.supplySource !== "public_catalog")
        return undefined;
    if (!Array.isArray(raw.findings) || !Array.isArray(raw.conclusions))
        return undefined;
    const findings = raw.findings.map(parseFinding).filter((f) => f !== null);
    const conclusions = raw.conclusions.map(parseConclusion).filter((c) => c !== null);
    // 任何一条解析失败就整份拒收：少一条结论比报错更危险（员工的「卡在哪一层」会被悄悄换掉）。
    if (findings.length !== raw.findings.length || conclusions.length !== raw.conclusions.length)
        return undefined;
    let tierReferrers = null;
    if (raw.tierReferrers !== null) {
        if (!Array.isArray(raw.tierReferrers))
            return undefined;
        tierReferrers = [];
        for (const r of raw.tierReferrers) {
            if (!isRecord(r) || !isStr(r.modelId) || !isStr(r.group) || !isStrList(r.tiers))
                return undefined;
            tierReferrers.push({ modelId: r.modelId, group: r.group, tiers: r.tiers });
        }
    }
    const handoff = raw.gatewayHandoff;
    if (!isRecord(handoff) || !isStr(handoff.modelId) || !isStrList(handoff.groups))
        return undefined;
    if (handoff.memberName !== undefined && !isStr(handoff.memberName))
        return undefined;
    let member;
    if (raw.member !== undefined) {
        const parsed = parseMember(raw.member);
        if (!parsed)
            return undefined;
        member = parsed;
    }
    for (const key of ["orgCode", "employee", "roleCode"]) {
        if (raw[key] !== undefined && !isStr(raw[key]))
            return undefined;
    }
    return {
        version: 1,
        modelId: raw.modelId,
        generatedAt: raw.generatedAt,
        catalogLive: raw.catalogLive,
        supplySource: raw.supplySource,
        ...(isStr(raw.orgCode) ? { orgCode: raw.orgCode } : {}),
        ...(isStr(raw.employee) ? { employee: raw.employee } : {}),
        ...(isStr(raw.roleCode) ? { roleCode: raw.roleCode } : {}),
        ...(member ? { member } : {}),
        findings,
        conclusions,
        tierReferrers,
        gatewayHandoff: {
            modelId: handoff.modelId,
            groups: handoff.groups,
            ...(isStr(handoff.memberName) ? { memberName: handoff.memberName } : {}),
        },
    };
}
const isGroupRef = (v) => isRecord(v) && typeof v.id === "number" && isStr(v.name);
function parseTierDefaults(v) {
    if (v === undefined || v === null)
        return undefined;
    if (!isRecord(v))
        return null;
    const out = {};
    for (const [group, tiers] of Object.entries(v)) {
        if (!isRecord(tiers))
            return null;
        const row = {};
        for (const tier of ["opus", "sonnet", "haiku"]) {
            if (tiers[tier] === undefined)
                continue;
            if (!isStr(tiers[tier]))
                return null;
            row[tier] = tiers[tier];
        }
        out[group] = row;
    }
    return out;
}
/**
 * 把一条 bundle / 清单行**按白名单逐字段拷贝**成诊断行——不 spread，凭证字段即便存在也带不出去。
 * 形状不对返回 null（调用方整份拒收）。`hasCredential` 由调用方按原行的 apiKey 判定后传入。
 */
export function toEmployeeBundleSnapshotRow(raw, hasCredential) {
    if (!isRecord(raw) || !isStr(raw.modelId) || !isStr(raw.protocol) || !isGroupRef(raw.chosenGroup))
        return null;
    if (!Array.isArray(raw.availableGroups) || !raw.availableGroups.every(isGroupRef))
        return null;
    const tierDefaultsByGroup = parseTierDefaults(raw.tierDefaultsByGroup);
    if (tierDefaultsByGroup === null)
        return null;
    const caps = isRecord(raw.capabilities) && typeof raw.capabilities.supportsToolUse === "boolean"
        ? { supportsToolUse: raw.capabilities.supportsToolUse }
        : undefined;
    return {
        modelId: raw.modelId,
        protocol: raw.protocol,
        vendor: isStr(raw.vendor) ? raw.vendor : "",
        family: isStr(raw.family) ? raw.family : "",
        ...(isStr(raw.familySlug) ? { familySlug: raw.familySlug } : {}),
        ...(typeof raw.translated === "boolean" ? { translated: raw.translated } : {}),
        ...(typeof raw.supported === "boolean" ? { supported: raw.supported } : {}),
        ...(typeof raw.available === "boolean" ? { available: raw.available } : {}),
        ...(isStr(raw.unavailableReason) ? { unavailableReason: raw.unavailableReason } : {}),
        chosenGroup: { id: raw.chosenGroup.id, name: raw.chosenGroup.name },
        availableGroups: raw.availableGroups.map((g) => ({ id: g.id, name: g.name })),
        ...(isStrList(raw.capabilityTags) ? { capabilityTags: [...raw.capabilityTags] } : {}),
        ...(caps ? { capabilities: caps } : {}),
        ...(tierDefaultsByGroup ? { tierDefaultsByGroup } : {}),
        hasCredential,
    };
}
/** admin-portal 收企业后端回包时的 guard（`{ success, data }` 信封由调用方先拆）。 */
export function parseEmployeeBundleSnapshot(raw) {
    if (!isRecord(raw) || !isStr(raw.employee))
        return undefined;
    const member = parseMember(raw.member);
    if (!member)
        return undefined;
    let rows = null;
    if (raw.rows !== null) {
        if (!Array.isArray(raw.rows))
            return undefined;
        rows = [];
        for (const r of raw.rows) {
            if (!isRecord(r) || typeof r.hasCredential !== "boolean")
                return undefined;
            const row = toEmployeeBundleSnapshotRow(r, r.hasCredential);
            if (!row)
                return undefined;
            rows.push(row);
        }
    }
    let fetchError;
    if (raw.fetchError !== undefined) {
        if (!isRecord(raw.fetchError) || !isStr(raw.fetchError.message))
            return undefined;
        fetchError = {
            message: raw.fetchError.message,
            ...(typeof raw.fetchError.httpStatus === "number" ? { httpStatus: raw.fetchError.httpStatus } : {}),
        };
    }
    return { employee: raw.employee, member, rows, ...(fetchError ? { fetchError } : {}) };
}
//# sourceMappingURL=model-availability-diagnosis.js.map